/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, Input, NgZone, OnInit} from '@angular/core';
import * as _ from 'lodash';

import template from './key-value-targeting.component.html';
import {ADN_TARGETING_TYPES} from '../targeting-constants';
import {TargetingHelper} from '../targeting-helper';
import {TargetingTemplateHook} from './targeting-template.component';
import {KeyValueSearchResource} from '../../../components/api/resources/resource/key-value-search';

interface KeyValueEntry {
  k: string;
  v: string;
  not: boolean;
}

interface KeyValueInputGroup {
  entries: KeyValueEntry[];
  notEntries: KeyValueEntry[];
}

@Component({
  standalone: false,
  selector: 'adn-key-value-targeting',
  template
})
export class KeyValueTargetingComponent implements OnInit {
  @Input() model: any[];
  @Input() tabController: any;
  @Input() overridableTargeting: any;
  @Input() targetType: any = ADN_TARGETING_TYPES.keyValue;
  @Input() underModel: any;
  @Input() mode: string;
  @Input() isLockedDown: boolean;
  @Input() mandatory: boolean;

  loaded = true;
  inputs: KeyValueInputGroup[] = [];
  list: any = {searchResults: [], totalHits: 0, pageStart: 0, pageEnd: 0};
  searchTerm = '';
  searchMeta = {
    pageSize: 50,
    currentPage: 1,
    resolved: true
  };

  readonly templateHook: TargetingTemplateHook = {
    getTemplateData: () => {
      this.prepareForSave();

      const mapEntries = _.map(_.get(this.model, [0, 'entries']), (val, key) => ({key, val}));
      const isSimple = this.model.length === 1 &&
        mapEntries.length === 1 &&
        mapEntries[0].val.length === 1 &&
        _.isEmpty(this.model[0].notEntries);
      const simpleValues = isSimple ? mapEntries[0].val[0] : null;
      const simpleSummary = isSimple
        ? (mapEntries[0].key + ': ' + (_.isEmpty(simpleValues) ? '(any value)' : simpleValues.join(', ')))
        : null;

      return {
        vm: undefined,
        model: _.cloneDeep(this.model),
        constant: ADN_TARGETING_TYPES.keyValue,
        summary: isSimple
          ? simpleSummary
          : TargetingHelper.getSizeOfTargetingTypes(this.model, ADN_TARGETING_TYPES.keyValue.targets) + ' key-values with logic'
      };
    },
    copyTemplate: (tpl: any) => this.copyTemplate(tpl)
  };

  constructor(
    private readonly keyValueSearchResource: KeyValueSearchResource,
    private readonly ngZone: NgZone,
    private readonly cdr: ChangeDetectorRef
  ) {
  }

  ngOnInit(): void {
    this.init();

    if (!this.tabController) {
      return;
    }

    const prepareForSave = () => this.prepareForSave();
    const copyTemplate = (tpl: any) => this.copyTemplate(tpl);
    this.tabController.register(ADN_TARGETING_TYPES.keyValue.widget, () => this.search(true), copyTemplate);
    this.tabController.callBeforeSubmit(prepareForSave);
    this.tabController.registerAllTabs(prepareForSave);
  }

  search(newSearch?: boolean): void {
    this.searchMeta.resolved = false;

    if (newSearch) {
      this.searchMeta.currentPage = 1;
    }

    this.keyValueSearchResource.query({
      q: this.searchTerm || '',
      pageSize: this.searchMeta.pageSize,
      page: this.searchMeta.currentPage - 1
    }).then((page: any) => {
      this.ngZone.run(() => {
        this.list = page;
        this.searchMeta.resolved = true;
        this.cdr.markForCheck();
      });
    });
  }

  changePage(page: number): void {
    if (page < 1) {
      return;
    }
    this.searchMeta.currentPage = page;
    this.search();
  }

  addOr(searchResult: any): void {
    this.checkAndCreateBaseInput();
    const theIndex = this.inputs.length - 1;
    const lastEntry = this.getLastEditableEntry(this.inputs[theIndex]);
    const values = this.stringifyEntryValues(searchResult.values);

    if (!lastEntry.k || lastEntry.k === searchResult.key) {
      lastEntry.k = searchResult.key;
      lastEntry.v = values;
    } else {
      this.addEntry(searchResult.key, values);
    }
  }

  addItem(key: string, value: string): void {
    this.checkAndCreateBaseInput();
    const theIndex = this.inputs.length - 1;
    const lastEntry = this.getLastEditableEntry(this.inputs[theIndex]);

    if (!lastEntry.k || lastEntry.k === key) {
      lastEntry.k = key;
      lastEntry.v = lastEntry.v ? (lastEntry.v + ', ' + value) : value;
    } else {
      this.addEntry(key, value);
    }
  }

  addAnd(searchResult: any): void {
    this.addRow(null, searchResult.key, this.stringifyEntryValues(searchResult.values));
  }

  addAndNot(searchResult: any): void {
    this.addNotRow(null, searchResult.key, this.stringifyEntryValues(searchResult.values));
  }

  addEntry(key?: string, value?: string): void {
    this.inputs.push({
      entries: [{k: key || '', v: value || '', not: false}],
      notEntries: []
    });
  }

  addRow(index?: number | null, key?: string, value?: string): void {
    const theIndex = _.isFinite(index) ? index as number : this.inputs.length - 1;
    this.inputs[theIndex].entries.push({k: key || '', v: value || '', not: false});
  }

  addNotRow(index?: number | null, key?: string, value?: string): void {
    const theIndex = _.isFinite(index) ? index as number : this.inputs.length - 1;
    this.inputs[theIndex].notEntries.push({k: key || '', v: value || '', not: false});
  }

  negateRow(parentIndex: number, index: number, isNot?: boolean): void {
    const param = isNot ? 'notEntries' : 'entries';
    this.inputs[parentIndex][param][index].not = !this.inputs[parentIndex][param][index].not;
  }

  removeRow(parentIndex: number, index: number, isNot?: boolean): void {
    this.inputs[parentIndex][isNot ? 'notEntries' : 'entries'].splice(index, 1);
    if (this.inputs[parentIndex].entries.length === 0 && this.inputs[parentIndex].notEntries.length === 0) {
      this.inputs.splice(parentIndex, 1);
    }
  }

  private init(): void {
    this.inputs = [];

    _.forEach(this.model || [], (entryObj: any) => {
      const entries: KeyValueEntry[] = [];
      const notEntries: KeyValueEntry[] = [];
      this.setUpEntries(entryObj.entries, entries);
      this.setUpEntries(entryObj.notEntries, notEntries);
      this.inputs.push({entries, notEntries});
    });

    this.checkAndCreateBaseInput();
  }

  private setUpEntries(data: any, savedData: KeyValueEntry[]): void {
    _.forEach(data, (v, k) => {
      const isNot = _.startsWith(String(k).toUpperCase(), 'NOT ');
      const nextLevelV = _.isArray(v[0]) ? v : [v];
      _.forEach(nextLevelV, (vLevel) => {
        savedData.push({
          k: isNot ? String(k).substr(4) : String(k),
          v: (vLevel || []).join(', '),
          not: isNot
        });
      });
    });
  }

  private checkAndCreateBaseInput(): void {
    if (!this.inputs[0] || !this.inputs[0].entries || this.inputs[0].entries.length === 0) {
      this.inputs[0] = this.inputs[0] || {entries: [], notEntries: []};
      this.inputs[0].entries = this.inputs[0].entries || [];
      this.inputs[0].entries.push({k: '', v: '', not: false});
      this.inputs[0].notEntries = this.inputs[0].notEntries || [];
    }
  }

  private getLastEditableEntry(group: KeyValueInputGroup): KeyValueEntry {
    if (group.notEntries.length > 0) {
      return group.notEntries[group.notEntries.length - 1];
    }
    return group.entries[group.entries.length - 1];
  }

  private stringifyEntryValues(values: any): string {
    if (_.isArray(values)) {
      return values.join(', ');
    }
    return values || '';
  }

  private prepareForSave(): void {
    this.model = this.model || [];
    this.model.length = 0;

    _.forEach(this.inputs, (theInput) => {
      const formattedForSave = {};
      const notFormattedForSave = {};
      let hasAnUpdate = false;

      const loopThrough = (param: 'entries' | 'notEntries', objForSave: any) => {
        _.forEach(theInput[param], (entry) => {
          const trimmedKeyOnly = _.trim(entry.k);
          const trimKey = (entry.not ? 'NOT ' : '') + trimmedKeyOnly;
          const trimValues = _.trim(this.stringifyEntryValues(entry.v));

          if (_.isString(trimmedKeyOnly) && trimmedKeyOnly.length > 0) {
            let mappedValues = [];
            if (_.isString(trimValues) && trimValues.length > 0) {
              const values = trimValues.split(',');
              mappedValues = _.filter(_.map(values, (v) => _.trim(v)), (v) => _.isString(v) && v.length > 0);
            }
            objForSave[trimKey] = objForSave[trimKey] || [];
            objForSave[trimKey].push(mappedValues);
            hasAnUpdate = true;
          }
        });
      };

      loopThrough('entries', formattedForSave);
      loopThrough('notEntries', notFormattedForSave);

      if (hasAnUpdate) {
        this.model.push({entries: formattedForSave, notEntries: notFormattedForSave});
      }
    });

    this.init();
  }

  private copyTemplate(tpl: any): void {
    const copied = _.cloneDeep(_.get(tpl, ['data', 'model'])) || [];
    this.model = this.model || [];
    this.model.length = 0;
    Array.prototype.push.apply(this.model, copied);
    this.init();
  }
}
